import type { ReactNode } from "react";
import { isAxiosError } from "axios";
import { Folder } from "lucide-react";
import { Button, Checkbox, LoadingSpinner } from "@/components/ui";
import type { useFileSearch } from "@/hooks/use-file-search";
import type { FileSearchHit } from "@/lib/data/schemas/files";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { cn } from "@/lib/utils/cn";
import { getFileIcon } from "@/lib/utils/matterFile";

type SearchState = ReturnType<typeof useFileSearch>;

export function FileSearchResults<
  T extends { id: string; name: string; idLocation?: string | null },
>({
  search,
  files,
  onSelect,
  selectedFileId,
  renderContextMenu,
  selectedIds,
  onToggleSelection,
  isSelectable = () => true,
  renderLocation,
}: {
  search: SearchState;
  files: T[];
  onSelect: (file: T) => void;
  selectedFileId?: string | null;
  renderContextMenu?: (file: T, trigger: ReactNode) => ReactNode;
  selectedIds?: Set<string>;
  onToggleSelection?: (file: T, checked: boolean) => void;
  isSelectable?: (file: T) => boolean;
  renderLocation?: (hit: FileSearchHit) => ReactNode;
}) {
  if (search.isTooShort) {
    return (
      <p role="status" className="text-muted-foreground px-3 py-4 text-sm">
        Enter at least 2 characters to search files.
      </p>
    );
  }
  if (search.isDebouncing || search.isLoading) {
    return (
      <div role="status" className="flex items-center gap-2 px-3 py-4 text-sm">
        <LoadingSpinner /> Searching files…
      </div>
    );
  }
  if (search.isError && !search.isFetchNextPageError) {
    return (
      <div role="alert" className="space-y-2 px-3 py-4 text-sm">
        <p>
          {isAxiosError(search.error)
            ? fileOperationMessage(
                search.error,
                "Unable to search files. Please try again.",
              )
            : "Unable to search files. Please try again."}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void search.refetch()}
        >
          Retry search
        </Button>
      </div>
    );
  }

  const byId = new Map(files.map((file) => [file.id, file]));
  const visibleHits = search.hits.filter((hit) => byId.has(hit.file.id));
  const getLocation = (parentId: string | null) => {
    const path: string[] = [];
    const visited = new Set<string>();
    while (parentId && !visited.has(parentId)) {
      visited.add(parentId);
      const folder = byId.get(parentId);
      if (!folder) {
        path.unshift("Nested folder");
        break;
      }
      path.unshift(folder.name);
      parentId = folder.idLocation || null;
    }
    return path.join(" / ") || "Root folder";
  };

  return (
    <div aria-label="File search results">
      {visibleHits.length === 0 ? (
        <p role="status" className="text-muted-foreground px-3 py-4 text-sm">
          No files found.
        </p>
      ) : (
        <ul className="space-y-0.5 px-1 py-1">
          {visibleHits.map((hit) => {
            const file = byId.get(hit.file.id);
            if (!file) return null;
            const Icon = getFileIcon(file.name);
            const selectable = isSelectable(file);
            const isSelected = selectedFileId === file.id;
            const trigger = (
              <div
                className={cn(
                  "flex items-start gap-2.5 rounded-md border border-transparent px-3 py-2 transition-colors",
                  selectable && "hover:bg-muted/60",
                  isSelected &&
                    "border-primary/15 bg-primary/5 hover:bg-primary/10",
                  !selectable && "opacity-50",
                )}
              >
                {onToggleSelection && selectedIds && (
                  <Checkbox
                    className="mt-0.5"
                    aria-label={`Select ${file.name}`}
                    checked={selectedIds.has(file.id)}
                    disabled={!selectable}
                    onCheckedChange={(checked) => {
                      onToggleSelection(file, checked === true);
                    }}
                  />
                )}
                <button
                  type="button"
                  className="focus-visible:ring-ring flex min-w-0 flex-1 items-start gap-2.5 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  aria-pressed={isSelected}
                  disabled={!selectable}
                  onClick={() => {
                    onSelect(file);
                  }}
                >
                  <Icon
                    aria-hidden="true"
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      isSelected ? "text-foreground" : "text-muted-foreground",
                    )}
                  />
                  <span className="min-w-0 flex-1 space-y-1">
                    <span className="line-clamp-2 text-sm leading-5 font-medium break-words">
                      {file.name}
                    </span>
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs leading-4">
                      <Folder aria-hidden="true" className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {renderLocation
                          ? renderLocation(hit)
                          : getLocation(hit.file.parentId)}
                      </span>
                      {hit.pageNumber !== null && (
                        <span className="shrink-0">
                          · Page {hit.pageNumber}
                        </span>
                      )}
                    </span>
                    {hit.excerpt && hit.excerpt !== file.name && (
                      <span className="text-muted-foreground line-clamp-2 text-xs leading-4">
                        {hit.excerpt}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            );
            return (
              <li key={file.id}>
                {renderContextMenu ? renderContextMenu(file, trigger) : trigger}
              </li>
            );
          })}
        </ul>
      )}
      {search.isFetchNextPageError && (
        <p role="alert" className="px-3 py-2 text-sm">
          Unable to load more files. Please try again.
        </p>
      )}
      {search.hasNextPage && (
        <div className="px-3 py-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={search.isFetching}
            onClick={() => void search.fetchNextPage()}
          >
            {search.isFetchingNextPage
              ? "Loading…"
              : search.isFetchNextPageError
                ? "Retry loading more"
                : "Load more files"}
          </Button>
        </div>
      )}
    </div>
  );
}
